import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import styles from './work4.module.scss';
import Contact from '../../components/Contact/Contact';

import heroImage from '../../assets/ex8.jpg';
import image1 from '../../assets/Work2-1.png';
import pdf from '../../assets/pdf.png';
import biddingwin from '../../assets/biddingwing_logo.png';
import biddingwinBlog from '../../assets/blog.png';
import icon1 from '../../assets/workicon2-1.png';
import icon2 from '../../assets/workicon2-2.png';
import icon3 from '../../assets/workicon2-3.png';
import SolutionDiagram from '../SolutionDiagram';
// PDF 파일 import 추가
import biddingwinProduct from '../../assets/biddingwin_product.pdf';

interface WorkImage {
  id: number;
  src: string;
  alt: string;
  text: string;
}

interface CircleFeature {
  id: number;
  src: string;
  alt: string;
  title: string;
  description: string;
  link?: string;
  isDownload?: boolean;
  downloadFile?: string;
}

interface ImageItem {
  id: number;
  src: string;
  alt: string;
  title: string;
  description: string;
}

const Work4: React.FC = () => {
  const { mode } = useSelector((state: RootState) => state.theme);
  const [imagesVisible, setImagesVisible] = useState<boolean[]>([false, false, false]);
  const [titleVisible, setTitleVisible] = useState<boolean>(false);
  const [descriptionVisible, setDescriptionVisible] = useState<boolean>(false);
  const [featuresVisible, setFeaturesVisible] = useState<boolean>(false);
  const [galleryVisible, setGalleryVisible] = useState<boolean>(false);
  const [sectionTitlesVisible, setSectionTitlesVisible] = useState<boolean[]>([false]);
  const imagesRef = useRef<(HTMLDivElement | null)[]>([]);
  const titleRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const sectionTitleRefs = useRef<(HTMLDivElement | null)[]>([null]);
  
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const slideRef = useRef<HTMLDivElement>(null);
  // 슬라이드 전환 상태를 추적하기 위한 ref 추가
  const isTransitioning = useRef(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkIsMobile();
    
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  const workImages: WorkImage[] = [
    {
      id: 1,
      src: image1,
      alt: "전자상거래 플랫폼 메인 화면",
      text: "아직 못정했습니다"
    }
  ];

  const circleFeatures: CircleFeature[] = [
    {
      id: 1,
      src: biddingwin,
      alt: "비딩윈 바로가기 버튼",
      title: "비딩윈 바로가기",
      description: "",
      link: "https://biddingwin.co.kr"
    },
    {
      id: 2,
      src: pdf,
      alt: "상품소개서 다운로드 버튼",
      title: "상품소개서 다운로드",
      description: "",
      isDownload: true,
      downloadFile: biddingwinProduct 
    },
    {
      id: 3,
      src: biddingwinBlog,
      alt: "비딩윈 블로그 바로가기 버튼",
      title: "비딩윈 블로그 바로가기",
      description: "",
      link: "https://blog.naver.com/biddingwin_"
    }
  ];

  // PDF 다운로드 함수
  const handleDownload = (filePath: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 링크 클릭 핸들러
  const handleFeatureClick = (feature: CircleFeature) => {
    if (feature.isDownload && feature.downloadFile) {
      handleDownload(feature.downloadFile, '비딩윈_상품소개서.pdf');
    } else if (feature.link) {
      window.open(feature.link, '_blank');
    }
  };


  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const titleObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTitleVisible(true);
          titleObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const featuresObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setFeaturesVisible(true);
          featuresObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const imageObservers = imagesRef.current.map((ref, index) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setImagesVisible(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
            observer.unobserve(entry.target);
          }
        });
      }, { ...observerOptions, threshold: 0.2 });
      return observer;
    });

    const galleryObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setGalleryVisible(true);
          galleryObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const descriptionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setDescriptionVisible(true);
          descriptionObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sectionTitleObservers = sectionTitleRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setSectionTitlesVisible(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);
      return observer;
    });

    if (titleRef.current) {
      titleObserver.observe(titleRef.current);
    }

    if (featuresRef.current) {
      featuresObserver.observe(featuresRef.current);
    }

    imagesRef.current.forEach((ref, index) => {
      if (ref) imageObservers[index].observe(ref);
    });

    if (galleryRef.current) {
      galleryObserver.observe(galleryRef.current);
    }

    if (descriptionRef.current) {
      descriptionObserver.observe(descriptionRef.current);
    }

    sectionTitleRefs.current.forEach((ref, index) => {
      if (ref) sectionTitleObservers[index].observe(ref);
    });

    return () => {
      if (titleRef.current) titleObserver.unobserve(titleRef.current);
      
      if (featuresRef.current) featuresObserver.unobserve(featuresRef.current);
      
      imagesRef.current.forEach((ref, index) => {
        if (ref) imageObservers[index].unobserve(ref);
      });
      
      if (galleryRef.current) galleryObserver.unobserve(galleryRef.current);
      
      if (descriptionRef.current) descriptionObserver.unobserve(descriptionRef.current);
      
      sectionTitleRefs.current.forEach((ref, index) => {
        if (ref) sectionTitleObservers[index].unobserve(ref);
      });
    };
  }, []);

  return (
    <div className={`${styles.work4Container} ${mode === 'dark' ? styles.dark : ''}`}>
        <div 
          ref={titleRef} 
          className={`${styles.titleSection} ${titleVisible ? styles.visible : ''}`}
        >
          <h1 className={styles.title}>
            <span className={styles.titleLine1}>네이버 파워링크/파워콘텐츠</span>
            <span className={styles.titleLine2}>자동입찰 솔루션</span>
            <span className={styles.titleFull}>네이버 파워링크/파워콘텐츠 자동입찰 솔루션</span>
          </h1>
          <div className={styles.overlay}></div>
          <img 
            src={heroImage} 
            alt="전자상거래 솔루션" 
            className={styles.backgroundImage} 
          />
        </div>
        <p className={styles.subtitle} 
           dangerouslySetInnerHTML={{ 
            __html: "13년 대행 경험과 6년 개발 노하우를 담은<br />광고대행사 맞춤형 자동입찰 솔루션<br />우리 회사에 필요한 기능만 골라<br /><span class=\"" + styles.impact + "\">minimize</span>와 나만의 시스템을 개발해보세요"
           }}>
        </p>
      
      <div
        ref={featuresRef}
        className={`${styles.featuresSection} ${featuresVisible ? styles.visible : ''}`}
      >
        <div 
        ref={el => {sectionTitleRefs.current[0] = el}} 
        className={`${styles.sectionTitle} ${sectionTitlesVisible[0] ? styles.visible : ''}`}
      >
        <h2>자세한 사항은 홈페이지를 확인하세요</h2>
      </div>
        <div className={styles.featuresContainer}>
          {circleFeatures.map((feature, index) => (
            <div 
              key={feature.id} 
              className={`${styles.featureItem} ${(feature.link || feature.isDownload) ? styles.clickable : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
              onClick={() => (feature.link || feature.isDownload) && handleFeatureClick(feature)}
            >
              <div className={styles.circleImageContainer}>
                <img 
                  src={feature.src} 
                  alt={feature.alt} 
                  className={`${styles.circleImage} ${
                    feature.id === 1 ? styles.biddingwinImage : 
                    feature.id === 2 ? styles.downloadImage : 
                    feature.id === 3 ? styles.blogImage : ''
                  }`} 
                />
              </div>
              <div className={styles.featureContent}>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>
                  {feature.description.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < feature.description.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
    
      
      <Contact 
        backgroundColor={mode === 'dark' ? '#2a2a2a' : '#f9f9f9'}
        textColor={mode === 'dark' ? '#f0f0f0' : '#333'}
      />
    </div>
  );
};

export default Work4;